import React, { useMemo, useState } from "react";
import { messagingEndpointsApi, userApi } from "../api/routes";
import { Button, Card, Input, Select, TextArea, Toast } from "../components/ui/Controls";
import { usePanelCache } from "../state/PanelCacheContext";

const emptyEndpoint = {
  userId: "",
  endpointId: "",
  channel: "TELEGRAM",
  externalId: "",
  status: "ACTIVE",
  primaryEndpoint: true,
  metadataJson: "",
};

export default function MessagingEndpointsPanel() {
  const [lookupUserId, setLookupUserId] = useState("");
  const [endpoint, setEndpoint] = useState(emptyEndpoint);
  const [mode, setMode] = useState("search");
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [panelState, setPanelState] = usePanelCache("messagingEndpoints");
  const results = panelState.results || [];
  const selectedUser = panelState.selectedUser || null;

  const currentTitle = useMemo(() => {
    if (!selectedUser) return "Messaging endpoints";
    return `Messaging endpoints • ${selectedUser.name || selectedUser.email || selectedUser.userId}`;
  }, [selectedUser]);

  const showToast = (message, tone = "success") => {
    setToast({ message, tone });
    setTimeout(() => setToast(null), 2500);
  };

  const loadForUser = async (userId) => {
    if (!userId) return;
    setLoading(true);
    try {
      const [userRes, endpointsRes] = await Promise.all([
        userApi.byId(userId),
        messagingEndpointsApi.listByUser(userId),
      ]);
      setPanelState({
        results: endpointsRes.data || [],
        selectedUser: userRes.data || null,
      });
    } catch (e) {
      showToast(e.message || "Failed to load endpoints", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    setLoading(true);
    try {
      const payload = {
        ...endpoint,
        primaryEndpoint: Boolean(endpoint.primaryEndpoint),
      };
      const res = await messagingEndpointsApi.create(payload);
      const created = res.data || payload;
      setPanelState({ results: [created, ...results] });
      setMode("search");
      setEndpoint({ ...emptyEndpoint, userId: endpoint.userId });
      showToast("Endpoint created");
    } catch (e) {
      showToast(e.message || "Failed to create endpoint", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    setLoading(true);
    try {
      const payload = {
        ...endpoint,
        primaryEndpoint: Boolean(endpoint.primaryEndpoint),
      };
      const res = await messagingEndpointsApi.update(endpoint.userId, endpoint.endpointId, payload);
      const updated = res.data || payload;
      setPanelState({
        results: results.map((item) =>
          item.endpointId === updated.endpointId && item.userId === updated.userId ? updated : item
        ),
      });
      setMode("search");
      showToast("Endpoint updated");
    } catch (e) {
      showToast(e.message || "Failed to update endpoint", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (item) => {
    setLoading(true);
    try {
      await messagingEndpointsApi.remove(item.userId, item.endpointId);
      setPanelState({
        results: results.filter((endpointItem) => endpointItem.endpointId !== item.endpointId),
      });
      showToast("Endpoint deleted");
    } catch (e) {
      showToast(e.message || "Failed to delete endpoint", "error");
    } finally {
      setLoading(false);
    }
  };

  const renderSearch = () => (
    <Card
      title={currentTitle}
      action={
        <div style={{ display: "flex", gap: 10 }}>
          <Button tone="ghost" onClick={() => setPanelState({ results: [], selectedUser: null })}>
            Clear
          </Button>
          <Button
            onClick={() => {
              setEndpoint({ ...emptyEndpoint, userId: lookupUserId || selectedUser?.userId || "" });
              setMode("edit");
            }}
          >
            + Add endpoint
          </Button>
        </div>
      }
    >
      <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 12, alignItems: "end" }}>
        <Input label="User ID" value={lookupUserId} onChange={setLookupUserId} placeholder="uuid" />
        <Button onClick={() => loadForUser(lookupUserId)} disabled={!lookupUserId} loading={loading}>Load endpoints</Button>
      </div>
      {results.length === 0 ? (
        <div className="empty-state">No endpoints loaded yet.</div>
      ) : (
        <div className="endpoint-grid">
          {results.map((item) => (
            <div key={`${item.userId}-${item.endpointId}`} className="endpoint-tile">
              <div className="endpoint-tile-header">
                <div>
                  <div className="endpoint-channel">{item.channel}</div>
                  <div className="endpoint-external">{item.externalId}</div>
                </div>
                <div className="endpoint-flags">
                  {item.primaryEndpoint ? <span className="ui-tag">Primary</span> : null}
                  <span className="ui-tag subtle">{item.status}</span>
                </div>
              </div>
              <div className="endpoint-meta">
                <div>Endpoint ID: <code>{item.endpointId}</code></div>
                <div>Verified: {item.verifiedAt || "Not yet"}</div>
                <div>Updated: {item.updatedAt || "Unknown"}</div>
              </div>
              <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
                <Button tone="ghost" onClick={() => { setEndpoint({ ...emptyEndpoint, ...item }); setMode("edit"); }}>
                  Edit
                </Button>
                <Button tone="secondary" onClick={() => handleDelete(item)}>
                  Delete
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );

  const renderEdit = () => (
    <Card
      title={endpoint.endpointId ? "Edit messaging endpoint" : "Create messaging endpoint"}
      action={<Button tone="ghost" onClick={() => setMode("search")}>← Back</Button>}
    >
      <div className="form-grid">
        <Input label="User ID" value={endpoint.userId} onChange={(value) => setEndpoint({ ...endpoint, userId: value })} />
        <Input label="Endpoint ID" value={endpoint.endpointId} onChange={(value) => setEndpoint({ ...endpoint, endpointId: value })} placeholder="optional" />
        <Select
          label="Channel"
          value={endpoint.channel}
          onChange={(value) => setEndpoint({ ...endpoint, channel: value })}
          options={[{ value: "TELEGRAM", label: "Telegram" }]}
        />
        <Input label="External ID" value={endpoint.externalId} onChange={(value) => setEndpoint({ ...endpoint, externalId: value })} />
        <Select
          label="Status"
          value={endpoint.status}
          onChange={(value) => setEndpoint({ ...endpoint, status: value })}
          options={["ACTIVE", "PENDING", "INACTIVE"]}
        />
        <Select
          label="Primary endpoint"
          value={String(endpoint.primaryEndpoint)}
          onChange={(value) => setEndpoint({ ...endpoint, primaryEndpoint: value === "true" })}
          options={[
            { value: "true", label: "Yes" },
            { value: "false", label: "No" },
          ]}
        />
      </div>
      <TextArea
        label="Metadata JSON"
        value={endpoint.metadataJson || ""}
        onChange={(value) => setEndpoint({ ...endpoint, metadataJson: value })}
        rows={6}
        placeholder='{"username":"runner"}'
      />
      <div style={{ display: "flex", gap: 12 }}>
        <Button onClick={endpoint.endpointId ? handleUpdate : handleCreate} loading={loading}>
          {endpoint.endpointId ? "Save changes" : "Create endpoint"}
        </Button>
      </div>
    </Card>
  );

  return (
    <>
      {mode === "search" ? renderSearch() : renderEdit()}
      <Toast toast={toast} onClose={() => setToast(null)} />
    </>
  );
}
